import React, { useMemo, useState } from 'react';
import { Button, Card, Carousel, Col, Input, Row, Select, Spin } from 'antd';
import * as actions from '@actions';
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io';
import { LoadingOutlined } from 'antd/icons';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import { ConfirmationModal } from 'vl-common/src/components/Modals';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import { useSessionAppointmentId } from '@hooks/useSessionAppointmentId';
import { useSessionFormState } from '@hooks/useSessionFormState';
import { useSessionOutcome } from '@hooks/useSessionOutcome';
import { UnstyledButton } from 'vl-common/src/components/UnstyledButton';
import { useEligibilityChecks } from '@hooks/useEligibilityChecks';
import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { CarouselRef } from 'ALIASED-antd/lib/carousel';
import useUser from '@vl-core/hooks/useUser';
import { CreateF2FTaskModal } from '@components/Tasks/CreateF2FTask';
import useAppointmentMeta from '@components/Clinicians/Session/hooks/useAppointmentMeta';
import { AuthorisationFormItem } from './AuthorisationFormItem';
import { SessionFormItem } from '../../Components/SessionFormItem';

const { Search } = Input;

const SelfBookAndF2fReferralFields = () => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const user = useUser();
  let carouselRef: CarouselRef | null = null;

  const apptId = useSessionAppointmentId();
  const { appointment } = useSessionAppointment();
  const { case_id: caseId, appointment_completed: isCompleted, read_only: readonly } = appointment;

  const [pageNo, setPageNo] = useState(0);
  const [selectedClinicianGuid, setSelectedClinicianGuid] = useState<string>();
  const [disabled, setDisabled] = useState(true);

  const [warnSaveModalOpen, setWarnSaveModalOpen] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');

  const [f2fOpen, setf2fOpen] = useState(false);

  const [auth] = useSessionFormState('auth');
  const [speciality, setSpeciality] = useSessionFormState('speciality');
  const [subSpeciality, setSubSpeciality] = useSessionFormState('subspeciality');
  const [reason, setReason] = useSessionFormState('ReasonForReferral');
  const [refClin, setRefClin] = useSessionFormState('refcli');
  const [postcode, setPostcode] = useSessionFormState('postcode');
  const [f2fComplete, setf2fComplete] = useSessionFormState('f2fComplete');
  const [, setF2fReferralPosted] = useSessionFormState('f2fReferralPosted');
  const [, setPostedReferralStartDate] = useSessionFormState('postedReferralStartDate');

  const { appointmentVersion, outcomeFlags, defaultAndDisableSelfBook } = useSessionOutcome();
  const { data: eligiblityChecks } = useEligibilityChecks();

  const outcomeHasSelfBookAuthOption = appointmentVersion === 1 ? !!outcomeFlags?.appt_auth_optional_flag : false;
  const outcomeHasF2fOption = appointmentVersion === 1 ? !!outcomeFlags?.appt_auth_f2f_flag : false;
  const outcomeHasF2fEligibleCheck =
    appointmentVersion === 1
      ? !!(outcomeFlags?.appt_f2f_consultant_flag && eligiblityChecks?.F2F_CONSULT_CHECK) ||
        !!(outcomeFlags?.appt_f2f_physio_flag && eligiblityChecks?.F2F_PHYSIO_CHECK)
      : false;

  const { data: appointmentsMeta } = useAppointmentMeta();
  const clientSpecAllowsF2fReferrals = !!appointmentsMeta?.f2f_referral;

  const { mutate: searchClinicians, data: refClinicians } = useMutation({
    mutationFn: async (postcode: string) => {
      if (!speciality) return [];

      const { clinicians } = await dispatch(actions.getRefClinicians(speciality, postcode, subSpeciality, caseId));

      return clinicians;
    },
    onMutate: setPostcode,
    onSuccess: () => setPageNo(0)
  });

  const clinicianPages = useMemo(() => {
    return Array.from({ length: Math.ceil(Number(refClinicians?.length) / 3) }, (_, i) =>
      refClinicians?.slice(i * 3, i * 3 + 3)
    );
  }, [refClinicians]);

  const toggle = () => {
    setDisabled(!disabled);
  };

  const { data: specialitiesMeta } = useSuspenseQuery({
    queryKey: ['specialitiesMeta', 1],
    queryFn: () => dispatch(actions.getSpecialities(1)),
    select: (data) => data?.cats,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });

  const { data: subSpecialitiesMeta } = useQuery({
    queryKey: ['subSpecialitiesMeta', speciality],
    queryFn: () => {
      if (!speciality) return Promise.reject();

      return dispatch(actions.getSubSpecialities(speciality));
    },
    select: (data) => data?.areas,
    enabled: !!speciality,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });

  const carouselArrow = (action: 'prev' | 'next') => {
    if (action === 'prev') {
      carouselRef?.prev();
    } else {
      carouselRef?.next();
    }
  };

  const outcomeRaisesTask = appointmentVersion === 1 ? outcomeFlags?.appt_raised_task_flag : false;
  const handleSave = () => {
    if (outcomeHasSelfBookAuthOption && (auth || defaultAndDisableSelfBook || outcomeRaisesTask)) {
      setWarningMessage('Once you have saved and the F2F referral has been created, these fields cannot be changed.');
      setWarnSaveModalOpen(true);
    } else {
      setf2fOpen(true);
    }
  };

  const { mutate: postF2FReferral, isPending: fieldsSaving } = useMutation({
    mutationFn: (request: Parameters<typeof actions.postReferral>[0]) => dispatch(actions.postReferral(request)),
    onSuccess: () => {
      sessionStorage.removeItem(`f2f-referral-${apptId}`);
      setF2fReferralPosted(true);
      setPostedReferralStartDate(appointment.auth_granted_start_date || undefined);
      setf2fComplete(true);
      setWarnSaveModalOpen(false);
    }
  });

  if (
    !(
      (outcomeHasSelfBookAuthOption && (!outcomeHasF2fOption || !clientSpecAllowsF2fReferrals)) ||
      (outcomeHasF2fOption && clientSpecAllowsF2fReferrals && !f2fComplete)
    )
  )
    return null;

  // Only show self book fields, as f2f is not allowed
  if (outcomeHasSelfBookAuthOption && !(outcomeHasF2fOption && clientSpecAllowsF2fReferrals)) {
    return <AuthorisationFormItem selfBookLabel="Allow patient to book their own follow-up" />;
  }

  /* Speciality only required for f2f consultant outcome */
  const showSpecialityInput = !outcomeRaisesTask;
  let cardTitle =
    appointmentVersion === 1 && outcomeFlags?.appt_f2f_consultant_flag ? t('F2FReferral') : 'Face to Face referral';
  cardTitle =
    appointmentVersion === 1 && outcomeFlags?.appt_f2f_physio_flag
      ? 'Face to Face Physio referral'
      : 'Face to Face referral';

  return (
    <Card size="small" title={cardTitle} css={{ marginBottom: 16 }} type="inner">
      {/* Both F2f and self-book are allowed */}
      {outcomeHasSelfBookAuthOption && !outcomeHasF2fEligibleCheck && (
        <AuthorisationFormItem selfBookLabel="Allow patient to book their own F2F appointment" />
      )}

      {showSpecialityInput && (
        <SessionFormItem name="speciality" label={t('Speciality')} required>
          <Select
            disabled={isCompleted || readonly}
            placeholder={t('Select from dropdown')}
            value={speciality}
            onSelect={(value) => {
              setSpeciality(value);
              setSubSpeciality(undefined);
              sessionStorage.setItem(
                `f2f-referral-${apptId}`,
                JSON.stringify({
                  speciality: value,
                  subSpeciality: null,
                  reason
                })
              );
            }}
            data-testid="speciality-select"
            options={specialitiesMeta?.map((res) => ({ label: res.cat_name, value: res.cat_code }))}
          />
        </SessionFormItem>
      )}

      {Number(specialitiesMeta?.find((spec) => spec?.cat_code === speciality)?.no_areas) > 0 && (
        <SessionFormItem name="subspeciality" label={t('Sub Speciality')} required preserve={false}>
          <Select
            disabled={isCompleted || readonly}
            placeholder={t('Select from dropdown')}
            value={subSpeciality}
            onSelect={(value) => {
              setSubSpeciality(value);
              sessionStorage.setItem(
                `f2f-referral-${apptId}`,
                JSON.stringify({
                  speciality,
                  subSpeciality: value,
                  reason
                })
              );
            }}
            data-testid="subspeciality-select"
            options={
              speciality
                ? subSpecialitiesMeta?.map((res) => ({
                    label: res.area_name,
                    value: res.area_code
                  }))
                : []
            }
          />
        </SessionFormItem>
      )}

      {/* F2f is allowed but self book isn't allowed or hasn't been granted */
      /* F2F Ascenti booked by admin through a task so must hide this section */}
      {(!outcomeHasSelfBookAuthOption ||
        (outcomeHasSelfBookAuthOption && !auth && !defaultAndDisableSelfBook) ||
        outcomeHasF2fEligibleCheck) &&
        !outcomeRaisesTask && (
          <>
            <SessionFormItem
              name="clinician_postcode"
              label={
                <div>
                  <div>{t('Select a Clinician')}</div>
                  <p>
                    <small>{t('The list will show the nearest clinicians to the given postcode')}</small>
                  </p>
                </div>
              }
              labelCol={{ span: 12 }}
            >
              <Search
                placeholder={t('Search postcode')}
                disabled={
                  readonly ||
                  isCompleted ||
                  !(speciality && (Number(subSpecialitiesMeta?.length) > 0 ? subSpeciality : true))
                }
                onSearch={(value) => {
                  searchClinicians(value);
                }}
                style={{ width: 200, float: 'right' }}
              />
            </SessionFormItem>

            <UnstyledButton
              type="button"
              onClick={() => {
                if (pageNo !== 0) {
                  carouselArrow('prev');
                  setPageNo(pageNo - 1);
                }
              }}
              css={{
                ...styles.arrow,
                float: 'left',
                right: '0px',
                opacity: pageNo === 0 ? 0.1 : 1
              }}
              data-testid="prev-button"
              aria-label={t('Previous')}
            >
              <IoIosArrowBack aria-hidden />
            </UnstyledButton>

            <UnstyledButton
              type="button"
              onClick={() => {
                if (clinicianPages.length > 1 && pageNo < clinicianPages.length - 1) {
                  carouselArrow('next');
                  setPageNo(pageNo + 1);
                }
              }}
              css={{
                ...styles.arrow,
                float: 'right',
                left: '0px',
                opacity: clinicianPages.length > 1 && pageNo < clinicianPages.length - 1 ? 1 : 0.1
              }}
              data-testid="next-button"
              aria-label={t('Next')}
            >
              <IoIosArrowForward aria-hidden />
            </UnstyledButton>

            <Carousel
              dots={false}
              ref={(ref) => {
                carouselRef = ref;
              }}
              infinite={false}
            >
              {clinicianPages?.map((page, index) => (
                <div key={index + 1 /* keeps SonarQube happy */}>
                  <Row style={styles?.clinicianPagesRow}>
                    {clinicianPages[index]?.map((item) => {
                      const selected = item.clin_user_guid === selectedClinicianGuid;

                      return (
                        <Col
                          key={item.clin_user_guid}
                          xs={24}
                          lg={24 / (clinicianPages[index]?.length || 1)}
                          css={{
                            ...styles?.clinicianPageColumn,
                            opacity: selectedClinicianGuid && !disabled && !selected ? 0.5 : 1,
                            border: selected ? '1px solid var(--productPrimary)' : '1px solid #ffffff'
                          }}
                        >
                          <div>
                            {item.title && <strong>{`${item.title} `}</strong>}
                            <strong>{`${item.first_name} ${item.last_name}`}</strong>
                            <br />
                            {item.subtype_desc}
                            <div style={{ margin: 5 }} />
                            <strong>{t('Specialised areas:')}</strong>
                            <br />
                            {item.spec_areas}
                            <br />
                            <div style={{ margin: 5 }} />
                            <strong>{t('Hospital:')}</strong>
                            <br />
                            {item.hospital_name}
                            <br />
                          </div>
                          <Button
                            disabled={readonly || !!(selectedClinicianGuid && !disabled && !selected)}
                            className={!disabled ? cx('answer-button', { active: selected }) : undefined}
                            onClick={() => {
                              if (!disabled && selected) {
                                setRefClin({});
                                setSelectedClinicianGuid(undefined);
                              } else {
                                setRefClin({
                                  ...item,
                                  postcode
                                });
                                setSelectedClinicianGuid(item.clin_user_guid);
                              }
                              toggle();
                            }}
                            data-testid="select-clinician-button"
                          >
                            {!disabled && selected ? t('Deselect') : t('Select')}
                          </Button>
                        </Col>
                      );
                    })}
                  </Row>
                </div>
              ))}
            </Carousel>
            {refClinicians?.length === 0 && (
              <div style={styles?.noClinicianContainer}>
                {t('There are no clinicians found in the area selected.')}
                <br />
                {t('No results found.')}
              </div>
            )}
          </>
        )}

      <SessionFormItem
        name="ReasonForReferral"
        css={{ marginBottom: 0, marginTop: 10 }}
        required
        label={t('Reason for referral')}
      >
        <Input
          css={{ marginBottom: 10, width: '100%', display: 'inline-block' }}
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            sessionStorage.setItem(
              `f2f-referral-${apptId}`,
              JSON.stringify({
                speciality,
                subSpeciality,
                reason: e.target.value
              })
            );
          }}
          disabled={isCompleted || readonly}
        />
      </SessionFormItem>
      <SessionFormItem css={{ marginBottom: 15, marginTop: 0 }}>
        <Button
          css={{ float: 'right' }}
          data-testid="save-referral-button"
          disabled={
            readonly ||
            isCompleted ||
            reason === '' ||
            (showSpecialityInput &&
              !(speciality && (Number(subSpecialitiesMeta?.length) > 0 ? subSpeciality : true))) ||
            fieldsSaving
          }
          type="link"
          size="small"
          className="book"
          onClick={handleSave}
        >
          {t('Save')}
          {fieldsSaving && <Spin indicator={<LoadingOutlined css={{ fontSize: 14, marginLeft: '4px' }} spin />} />}
        </Button>
      </SessionFormItem>

      <CreateF2FTaskModal open={f2fOpen} onClose={() => setf2fOpen(false)} />

      <ConfirmationModal
        open={warnSaveModalOpen}
        onCancel={() => setWarnSaveModalOpen(false)}
        footer={[
          <Button
            key="cancel-save-f2f"
            data-testid="cancel-save-f2f-button"
            onClick={() => setWarnSaveModalOpen(false)}
          >
            {t('Cancel')}
          </Button>,
          <Button
            type="primary"
            key="confirm-save-f2f"
            data-testid="confirm-save-f2f-button"
            onClick={() => {
              postF2FReferral({
                user_guid: user?.user_guid,
                appt_id: appointment.appointment_id,
                refclin_guid: refClin ? refClin.clin_user_guid : '',
                speccat: speciality || appointment.spec_cat_code,
                specarea: subSpeciality || appointment.case_spec_area_code || undefined,
                hospital_id: refClin ? refClin.hospital_id : undefined,
                reason: reason ?? '',
                postcode: refClin ? refClin.postcode : ''
              });
            }}
          >
            {t('Save')}
          </Button>
        ]}
        closable
        title="Are you sure you want to save?"
      >
        <>{warningMessage}</>
      </ConfirmationModal>
    </Card>
  );
};

const styles: Record<string, React.CSSProperties> = {
  arrow: {
    position: 'relative',
    fontSize: '24px',
    color: 'var(--productGray)',
    cursor: 'pointer',
    top: '75px',
    zIndex: 10
  },
  clinicianPageColumn: {
    display: 'grid',
    placeItems: 'center',
    textAlign: 'center',
    borderRadius: '5px',
    height: 200,
    fontSize: 11,
    color: 'black'
  },
  clinicianPagesRow: {
    padding: '5px',
    borderRadius: '5px',
    margin: '5px'
  },
  noClinicianContainer: {
    display: 'grid',
    placeItems: 'center',
    textAlign: 'center',
    padding: '5px',
    borderRadius: '5px',
    margin: '5px',
    height: 150
  }
};

export default SelfBookAndF2fReferralFields;
